import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { faCircleArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Spinner } from "@performyard/py-components";

import { useAskStream } from "../hooks/useAskStream";
import { useFormAuthoringCoachEnabled } from "../hooks/useFormAuthoringCoachEnabled";
import { useIsAuthoringReviewForm } from "../hooks/useIsAuthoringReviewForm";

import { isUploadInFlight } from "../utils/is-upload-in-flight";

import { useAskPerformYardStore } from "../AskPerformYardStore";

// Maximum character limit for user input
const MAX_INPUT_LENGTH = 4000;

type ChatInputProps = {
	/** "panel" restyles for the docked Ask PY panel (pill container, circular send). Default keeps the floating chat look. */
	variant?: "floating" | "panel";
};

export const ChatInput: React.FC<ChatInputProps> = ({ variant = "floating" }) => {
	const isPanel = variant === "panel";
	const inputValue = useAskPerformYardStore((state) => state.inputValue);
	const setInputValue = useAskPerformYardStore((state) => state.setInputValue);
	const status = useAskPerformYardStore((state) => state.status);
	const messages = useAskPerformYardStore((state) => state.messages);
	const scrollToBottom = useAskPerformYardStore((state) => state.scrollToBottom);
	const hasNoDocuments = useAskPerformYardStore((state) => state.hasNoDocuments);
	const askHrEnabled = useAskPerformYardStore((state) => state.cachedAskHrEnabled);
	const goalCreationEnabled = useAskPerformYardStore((state) => state.cachedGoalCreationEnabled);
	const formCreationEnabled = useAskPerformYardStore((state) => state.cachedFormCreationEnabled);
	const goalCoachingEnabled = useAskPerformYardStore((state) => state.cachedGoalCoachingEnabled);
	const formBuildingCoachEnabled = useAskPerformYardStore((state) => state.cachedFormBuildingCoachEnabled);
	const isOnGoalCreationPage = useAskPerformYardStore((state) => state.isOnGoalCreationPage);
	const isOnFormBuilderPage = useAskPerformYardStore((state) => state.isOnFormBuilderPage);
	// Live hooks, as in AskPyPanel: the coach has no cached flag, and its availability is what decides
	// whether there is anything to type to here.
	const formAuthoringCoachEnabled = useFormAuthoringCoachEnabled();
	const isAuthoringReviewForm = useIsAuthoringReviewForm();
	const { sendQuery, isStreaming } = useAskStream();
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const wasStreamingRef = useRef(false);

	// Only disable for no-documents when Ask HR is on and no other capability can compensate
	const isBlockedByNoDocuments =
		askHrEnabled &&
		hasNoDocuments &&
		!goalCreationEnabled &&
		!formCreationEnabled &&
		!(isOnGoalCreationPage && goalCoachingEnabled) &&
		!(isOnFormBuilderPage && formBuildingCoachEnabled) &&
		// Keeps this in step with the panel, which shows the Form Authoring Coach action instead of the
		// no-documents notice while a review is being authored. Panel-scoped, because that action is:
		// the FAB never offers the coach, so on its surface a review page really has nothing to type to.
		!(isPanel && isAuthoringReviewForm && formAuthoringCoachEnabled);
	const isUploading = isUploadInFlight(messages);
	const isDisabled = isStreaming || status === "error" || isBlockedByNoDocuments || isUploading;
	const canSend = inputValue.trim().length > 0 && !isDisabled;

	const handleSubmit = useCallback(
		(e?: React.FormEvent) => {
			e?.preventDefault();

			if (!canSend) return;

			const query = inputValue.trim();

			// Get conversation history for context (excluding greeting and current input)
			const conversationHistory = messages
				.filter((m) => m.id !== "greeting")
				.map((m) => ({
					role: m.role as "user" | "assistant",
					content: m.content,
				}));

			sendQuery({
				query,
				messages: conversationHistory,
			});

			// Scroll to bottom when sending a message
			scrollToBottom?.();
		},
		[canSend, inputValue, messages, sendQuery, scrollToBottom]
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			// Submit on Enter (without Shift)
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSubmit();
			}
		},
		[handleSubmit]
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			setInputValue(e.target.value);
		},
		[setInputValue]
	);

	// Auto-resize textarea when content changes
	useLayoutEffect(() => {
		const textarea = textareaRef.current;
		if (textarea) {
			textarea.style.height = "auto";
			textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
		}
	}, [inputValue]);

	// Autofocus textarea when panel opens
	useEffect(() => {
		textareaRef.current?.focus();
	}, []);

	// Refocus textarea when streaming completes
	useEffect(() => {
		if (wasStreamingRef.current && !isStreaming) {
			textareaRef.current?.focus();
		}
		wasStreamingRef.current = isStreaming;
	}, [isStreaming]);

	return (
		<div className={`flex flex-col gap-2 pb-4 ${isPanel ? "px-3 pt-0" : "px-4 pt-3"}`}>
			<form
				onSubmit={handleSubmit}
				className={
					isPanel
						? `box-border flex w-full items-end gap-2 rounded-lg border border-solid border-neutral-3 p-2 transition-colors focus-within:border-primary-5 hover:border-neutral-5 focus-within:hover:border-primary-5 ${isDisabled ? "bg-neutral-1" : "bg-white"}`
						: "box-border flex w-full items-end gap-2"
				}
			>
				<div className="box-border flex flex-1 flex-col items-start gap-2">
					<textarea
						ref={textareaRef}
						value={inputValue}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						placeholder={isPanel ? "Ask PerformYard..." : "Ask PerformYard"}
						disabled={isDisabled}
						// font-[inherit]: textareas default to monospace, so the app font has to be forced through.
						className={
							isPanel
								? "box-border h-6 max-h-[120px] min-h-6 w-full resize-none overflow-hidden border-none bg-transparent px-1 py-1 font-[inherit] text-sm leading-4 placeholder:text-neutral-5 hover:overflow-y-auto focus:overflow-y-auto focus:outline-hidden disabled:cursor-not-allowed [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:bg-transparent"
								: "box-border h-[36px] max-h-[120px] min-h-[36px] w-full resize-none overflow-hidden rounded border border-neutral-300 bg-white px-3 py-2 font-[inherit] text-sm placeholder:text-neutral-400 hover:overflow-y-auto focus:overflow-y-auto focus:border-transparent focus:ring-2 focus:ring-primary-5 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-neutral-50 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-track]:bg-transparent"
						}
						rows={1}
						maxLength={MAX_INPUT_LENGTH}
						aria-label="Ask a question"
					/>
				</div>
				<button
					type="submit"
					disabled={!canSend}
					aria-label="Send message"
					className={
						isPanel
							? `flex size-6 shrink-0 items-center justify-center rounded-full border-none bg-transparent p-0 transition-colors ${
									canSend
										? "cursor-pointer text-primary-5 hover:text-primary-7 active:text-primary-8"
										: "cursor-not-allowed text-neutral-2"
								}`
							: `flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded border-none bg-primary-5 transition-all ${
									canSend
										? "cursor-pointer hover:bg-primary-7 active:bg-primary-8"
										: "cursor-not-allowed bg-neutral-300"
								}`
					}
				>
					{isPanel && isStreaming ? (
						<Spinner size={16} />
					) : isPanel ? (
						// text-[24px]: FA's injected height:1em rule is unlayered so it beats layered
						// utilities like h-6 — the glyph must be sized through font-size to hit 24px.
						<FontAwesomeIcon icon={faCircleArrowUp} className="h-6 w-6 text-[24px]" />
					) : (
						<FontAwesomeIcon icon={faPaperPlane} className="text-base text-white" />
					)}
				</button>
			</form>

			<p className="w-full text-center text-xs text-neutral-5">
				AI may make mistakes. Please verify important information.
			</p>
		</div>
	);
};

export default ChatInput;
